import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageURL } from '../hooks/useLanguageURL';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const { setLanguageInURL } = useLanguageURL();
  const [currentLanguage, setCurrentLanguage] = useState('ru');
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize language only once
  useEffect(() => {
    if (isInitialized) return;

    // Get language from URL directly
    const urlParams = new URLSearchParams(window.location.search);
    const urlLanguage = urlParams.get('lang');
    const savedLanguage = localStorage.getItem('language');
    
    let languageToUse = 'ru';
    
    // Priority: URL parameter > localStorage > default
    if (urlLanguage === 'ru' || urlLanguage === 'en') {
      languageToUse = urlLanguage;
    } else if (savedLanguage && (savedLanguage === 'ru' || savedLanguage === 'en')) {
      languageToUse = savedLanguage;
      // Update URL with saved language
      setLanguageInURL(savedLanguage);
    } else {
      // Set default language in URL
      setLanguageInURL('ru');
    }
    
    setCurrentLanguage(languageToUse);
    i18n.changeLanguage(languageToUse);
    localStorage.setItem('language', languageToUse);
    setIsInitialized(true);
  }, [isInitialized, setLanguageInURL, i18n]); // Add back necessary dependencies

  // Sync language when URL changes (but only after initialization)
  useEffect(() => {
    if (!isInitialized) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const urlLanguage = urlParams.get('lang');
    
    if (urlLanguage === 'ru' || urlLanguage === 'en') {
      if (urlLanguage !== currentLanguage) {
        setCurrentLanguage(urlLanguage);
        i18n.changeLanguage(urlLanguage);
        localStorage.setItem('language', urlLanguage);
      }
    }
  }, [window.location.search, isInitialized, currentLanguage, i18n]);

  // Function to change language
  const changeLanguage = useCallback((language) => {
    if (language === 'ru' || language === 'en') {
      setCurrentLanguage(language);
      i18n.changeLanguage(language);
      localStorage.setItem('language', language);
      setLanguageInURL(language);
    }
  }, [i18n, setLanguageInURL]);

  // Function to toggle between languages
  const toggleLanguage = useCallback(() => {
    const newLanguage = currentLanguage === 'ru' ? 'en' : 'ru';
    changeLanguage(newLanguage);
  }, [currentLanguage, changeLanguage]);

  const value = {
    currentLanguage,
    changeLanguage,
    toggleLanguage,
    isRussian: currentLanguage === 'ru',
    isEnglish: currentLanguage === 'en'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}; 